// Centralized map data — single source of truth for radar images and coordinate configs.
// When adding a new map: import the PNG, add to mapImageMappings, and add its config.
// Run `npm run update-radars` to fetch latest radar PNGs from MurkyYT/cs2-map-icons.

import de_inferno from './de_inferno.png';
import de_mirage from './de_mirage.png';
import de_nuke from './de_nuke.png';
import de_nuke_lower from './de_nuke_lower.png';
import de_overpass from './de_overpass.png';
import de_ancient from './de_ancient.png';
import de_vertigo from './de_vertigo.png';
import de_anubis from './de_anubis.png';
import de_dust2 from './de_dust2.png';
import de_train from './de_train.png';
import de_cache from './de_cache.png';

/** Radar image imports keyed by internal map name (de_*) */
export const mapImageMappings: Record<string, string> = {
    de_inferno, de_mirage, de_nuke, de_nuke_lower, de_overpass,
    de_vertigo, de_ancient, de_anubis, de_dust2, de_train, de_cache,
};

/**
 * Radar coordinate configs from CS2 resource/overviews/*.txt files.
 * pos_x, pos_y: world-space origin offset
 * scale: world units per radar pixel (1024x1024 normalized)
 */
export const mapConfigs: Record<string, { pos_x: number; pos_y: number; scale: number }> = {
    de_dust2:   { pos_x: -2476, pos_y: 3239, scale: 4.4 },
    de_mirage:  { pos_x: -3230, pos_y: 1713, scale: 5 },
    de_inferno: { pos_x: -2087, pos_y: 3870, scale: 4.9 },
    de_ancient: { pos_x: -2953, pos_y: 2164, scale: 5 },
    de_anubis:  { pos_x: -2796, pos_y: 3328, scale: 5.22 },
    de_nuke:    { pos_x: -3453, pos_y: 2887, scale: 7 },
    de_overpass:{ pos_x: -4831, pos_y: 1781, scale: 5.2 },
    de_vertigo: { pos_x: -3168, pos_y: 1762, scale: 4 },
    de_train:   { pos_x: -2308, pos_y: 2078, scale: 4.082077 },
    // Cache is not on MurkyYT/cs2-map-icons, so its radar is sourced manually and is
    // NOT refreshed by `npm run update-radars`. Values are Valve's own, from
    // csgo/resource/overviews/de_cache.txt, verified against the radar to ~2px.
    de_cache:   { pos_x: -2000, pos_y: 3250, scale: 5.5 },
};

/** Configuration for maps with multiple vertical levels (e.g. Nuke upper/lower) */
export interface MultiLevelMapConfig {
    upperImage: string;
    lowerImage: string;
    // Z thresholds for hard upper/lower classification
    zUpper: number;     // Z >= this AND outside bothZone → upper only
    zLower: number;     // Z <= this → lower only
    // Optional XY box where players in the Z transition range show on both panels (ramp/stairs)
    bothZone?: { minX: number; maxX: number; minY: number; maxY: number };
    // Crop regions in radar-pixel space (0-1024)
    upperCrop: { x: number; y: number; w: number; h: number };
    lowerCrop: { x: number; y: number; w: number; h: number };
}

export const multiLevelMaps: Record<string, MultiLevelMapConfig> = {
    de_nuke: {
        upperImage: 'de_nuke',
        lowerImage: 'de_nuke_lower',
        zUpper: -495,   // Z >= this: upper only (unless in bothZone)
        zLower: -620,   // Z <= this: lower only
        bothZone: { minX: 75, maxX: 1265, minY: -116, maxY: 927 }, // ramp/stairs area
        upperCrop: { x: 0, y: 80, w: 960, h: 860 },
        lowerCrop: { x: 460, y: 0, w: 280, h: 775 },
    },
};
